/* eslint-disable react/no-array-index-key */
import React from 'react';
import { Document, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';

import type { BidderReportData, ReportBlock, ReportSection } from './report_data';

export * from './report_data';

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingBottom: 56,
    paddingHorizontal: 44,
    fontSize: 10,
    color: '#212529',
    lineHeight: 1.45,
  },
  header: {
    borderBottomWidth: 2,
    borderBottomColor: '#007bff',
    paddingBottom: 10,
    marginBottom: 18,
  },
  headerTitle: {
    fontSize: 16,
    color: '#21354a',
  },
  headerSubtitle: {
    fontSize: 10,
    color: '#495057',
    marginTop: 4,
  },
  headerMeta: {
    fontSize: 9,
    color: '#6c757d',
    marginTop: 2,
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 12,
    color: '#21354a',
    backgroundColor: '#f4f5f7',
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginBottom: 8,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  rowLabel: {
    width: '34%',
    paddingRight: 10,
    color: '#6c757d',
  },
  rowValue: {
    flex: 1,
    color: '#212529',
  },
  listItem: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  listBullet: {
    width: 12,
    color: '#007bff',
  },
  listText: {
    flex: 1,
  },
  paragraph: {
    marginBottom: 6,
  },
  table: {
    borderWidth: 1,
    borderColor: '#dee2e6',
    borderRadius: 4,
    marginTop: 4,
    marginBottom: 6,
    fontSize: 8,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#e9ecef',
    borderBottomWidth: 1,
    borderBottomColor: '#ced4da',
  },
  tableHeaderCell: {
    paddingVertical: 5,
    paddingHorizontal: 5,
    color: '#21354a',
    fontFamily: 'Helvetica-Bold',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  tableCell: {
    paddingVertical: 4,
    paddingHorizontal: 5,
  },
  footer: {
    position: 'absolute',
    bottom: 24,
    left: 44,
    right: 44,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#adb5bd',
  },
});

const ReportBlockView: React.FC<{ block: ReportBlock }> = ({ block }) => {
  if (block.kind === 'rows') {
    return (
      <>
        {block.rows.map((row) => (
          <View style={styles.row} key={`${row.label}-${row.value}`} wrap={false}>
            <Text style={styles.rowLabel}>{row.label}</Text>
            <Text style={styles.rowValue}>{row.value}</Text>
          </View>
        ))}
      </>
    );
  }

  if (block.kind === 'table') {
    const cellStyle = (index: number) => ({
      width: `${block.columns[index].width}%`,
      textAlign: block.columns[index].align ?? 'left',
    });

    return (
      <View style={styles.table}>
        <View style={styles.tableHeader} wrap={false}>
          {block.columns.map((column, index) => (
            <Text key={column.label} style={[styles.tableHeaderCell, cellStyle(index)]}>
              {column.label}
            </Text>
          ))}
        </View>
        {block.rows.map((row, rowIndex) => (
          <View
            key={rowIndex}
            style={
              rowIndex === block.rows.length - 1
                ? [styles.tableRow, { borderBottomWidth: 0 }]
                : styles.tableRow
            }
            wrap={false}
          >
            {row.map((cell, index) => (
              <Text key={index} style={[styles.tableCell, cellStyle(index)]}>
                {cell}
              </Text>
            ))}
          </View>
        ))}
      </View>
    );
  }

  if (block.kind === 'list') {
    return (
      <>
        {block.items.map((item, index) => (
          <View style={styles.listItem} key={`${index}-${item.slice(0, 24)}`}>
            <Text style={styles.listBullet}>•</Text>
            <Text style={styles.listText}>{item}</Text>
          </View>
        ))}
      </>
    );
  }

  return <Text style={styles.paragraph}>{block.text}</Text>;
};

const ReportSectionView: React.FC<{ section: ReportSection }> = ({ section }) => (
  <View style={styles.section}>
    <Text style={styles.sectionTitle}>{section.title}</Text>
    {section.blocks.map((block, index) => (
      <ReportBlockView block={block} key={`${section.title}-${index}`} />
    ))}
  </View>
);

const BidderReportDocument: React.FC<{ data: BidderReportData }> = ({ data }) => (
  <Document title={`Relatório da licitação ${data.biddingNumber}`}>
    <Page size="A4" style={styles.page}>
      <View style={styles.header} fixed>
        <Text style={styles.headerTitle}>Relatório da licitação {data.biddingNumber}</Text>
        {data.publicBody ? <Text style={styles.headerSubtitle}>{data.publicBody}</Text> : null}
        <Text style={styles.headerMeta}>
          Nº Conlicitação: {data.conlicitacaoId} · Gerado em {data.generatedAt}
        </Text>
      </View>

      {data.sections.map((section) => (
        <ReportSectionView section={section} key={section.title} />
      ))}

      <View style={styles.footer} fixed>
        <Text>Conlicitação · Analista de Editais</Text>
        <Text render={({ pageNumber, totalPages }) => `${pageNumber} de ${totalPages}`} />
      </View>
    </Page>
  </Document>
);

const sanitizeFileName = (name: string): string =>
  name
    .replace(/[\\/]+/g, '-')
    // eslint-disable-next-line no-control-regex
    .replace(/[<>:"|?*\u0000-\u001f]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^[\s.-]+|[\s.-]+$/g, '')
    .slice(0, 120)
    .trim() || 'Relatório do Licitante';

export const downloadBidderReportPdf = async (
  data: BidderReportData,
  filenameBase: string,
): Promise<{ blob: Blob; filename: string } | null> => {
  try {
    const blob = await pdf(<BidderReportDocument data={data} />).toBlob();
    const filename = `${sanitizeFileName(filenameBase)}.pdf`;
    saveAs(blob, filename);
    return { blob, filename };
  } catch (err) {
    console.error('Erro ao gerar o PDF do relatório do licitante:', err);
    return null;
  }
};

export default BidderReportDocument;
